/**
 * THE CAPABILITY LIBRARY — ONE INVENTORY, TWO ORIGINS.
 *
 * PLATFORM capabilities are instruments this build provides: SEARCH, READ_PAGE,
 * CORPUS, the trial broker, the Foundry tool itself. FOUNDRY capabilities are
 * the ones Scout or the Owner registered into the durable registry. They are
 * different kinds of fact — one is compiled into the runtime, the other is a
 * stored descriptor with versions and executions — and the table keeps that
 * distinction visible rather than flattening it into one "tool" list.
 *
 * THE AVAILABILITY COLUMN IS THE RUNTIME'S ANSWER. A platform fact carries the
 * deployment's own available/state pair; a Foundry capability carries its
 * registry status, delivery, and the run aggregate the gateway projected. A
 * capability with no run is not displayed as "executed successfully"; a shared
 * capability is not displayed as globally executable unless the gateway's
 * availability evidence says so.
 *
 * THE DETAIL PANE READS THE SAME ROW. Selecting a row is a route, so a deep
 * link opens the same capability; the pane never holds a second copy of the
 * inventory.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable } from '../resource';
import { readGateway, type GatewayError } from '../gateway';
import { Failure } from './failure';
import { PageHeader, Technical } from './about';
import { navigate } from '../router';
import { formatTurnTime } from '../chat';
import type { ComponentChildren } from 'preact';

interface PlatformFact {
	name: string;
	does: string;
	invocation: string;
	returns: string;
	available: boolean;
	state: string;
	recovery: string;
	origin: 'PLATFORM';
}

interface Execution {
	total: number;
	succeeded: number;
	failed: number;
	refused: number;
	lastRunAt: string | null;
	lastStatus: string | null;
}

interface Availability {
	globalAvailability: string;
	environment: string;
	delivery: string;
	coreIntegrated: boolean;
	mainScout?: { executable: boolean; manifestReachable: boolean; executedSuccessfully: boolean; scope: string };
	researchSpaces?: {
		executable: boolean;
		manifestReachable: boolean;
		executedSuccessfully: boolean;
		scope: string;
	};
	ownerActionRequired: boolean;
	ownerActionNote: string;
	evidence: string[];
}

interface FoundryCapability {
	capabilityId: string;
	spaceId: string | null;
	name: string;
	purpose: string;
	whenToUse: string;
	status: string;
	origin: string;
	shared: boolean;
	delivery: string;
	version: number;
	sourceHash: string;
	network: string;
	dependencies: string[];
	limits: { timeoutMs?: number; memoryMb?: number } | null;
	limitations: string;
	updatedAt: string;
	lastVerifiedAt: string | null;
	availability?: Availability;
	execution?: Execution | null;
	reviewLifecycle?: string;
}

interface Workspace {
	spaceId: string;
	name: string;
	slug: string;
	status: string;
}

interface LibraryPayload {
	ok: boolean;
	wired: boolean;
	reason?: string;
	platform: PlatformFact[] | null;
	platformReason: string | null;
	foundry: FoundryCapability[];
	foundryReason: string | null;
	workspaces: Workspace[];
	summary?: {
		platformTotal: number;
		platformAvailable: number;
		foundryTotal: number;
		foundryVerified: number;
		foundryExecuted: number;
	};
}

interface FoundryRun {
	runId: string;
	capabilityId: string;
	capabilityVersion: number;
	status: string;
	createdAt: string;
	completedAt: string | null;
	durationMs: number | null;
	refusalReason: string | null;
}

interface FoundryDetailPayload {
	ok: boolean;
	wired: boolean;
	reason?: string;
	capability?: FoundryCapability & {
		inputSchema?: unknown;
		outputSchema?: unknown;
		sourceCode?: string;
		example?: unknown;
	};
	runs?: FoundryRun[];
	versions?: Array<{ version: number; sourceHash: string; createdAt: string }>;
	adoption?: unknown;
	reviews?: Array<{ reviewId: string; state: string; verdict?: string | null; createdAt?: string }>;
}

type Row =
	| ({ kind: 'PLATFORM' } & PlatformFact)
	| ({ kind: 'FOUNDRY' } & FoundryCapability);

function originLabel(row: Row): string {
	return row.kind === 'PLATFORM' ? 'PLATFORM' : row.origin.replaceAll('_', ' ');
}

function statusLabel(row: Row): string {
	return row.kind === 'PLATFORM' ? (row.available ? 'AVAILABLE' : 'UNAVAILABLE') : row.status;
}

function executableLabel(row: Row): string {
	if (row.kind === 'PLATFORM') return row.available ? 'Callable now' : row.state;
	const availability = row.availability;
	if (availability === undefined) return 'not projected';
	return availability.globalAvailability;
}

function scopeLabel(row: Row, workspaces: Workspace[]): string {
	if (row.kind === 'PLATFORM') return 'Every workspace';
	if (row.shared || row.delivery === 'SHARED_REGISTRY') return 'Shared registry';
	if (row.spaceId === null) return 'Main Scout';
	return workspaces.find((space) => space.spaceId === row.spaceId)?.name ?? row.spaceId;
}

function predicatePasses(row: Row, query: string, origin: string, state: string): boolean {
	const wanted = query.trim().toLowerCase();
	if (
		wanted.length > 0 &&
		!`${row.name} ${row.kind === 'PLATFORM' ? row.does : `${row.purpose} ${row.whenToUse}`}`
			.toLowerCase()
			.includes(wanted)
	) {
		return false;
	}
	if (origin !== 'all' && row.kind !== origin) return false;
	if (state === 'all') return true;
	if (state === 'available') return row.kind === 'PLATFORM' ? row.available : row.status === 'VERIFIED';
	if (state === 'unavailable') return row.kind === 'PLATFORM' ? !row.available : row.status !== 'VERIFIED';
	if (state === 'executed') return row.kind === 'FOUNDRY' && (row.execution?.total ?? 0) > 0;
	if (state === 'never-run') return row.kind === 'FOUNDRY' && (row.execution?.total ?? 0) === 0;
	return true;
}

function DetailField({ label, children }: { label: string; children: ComponentChildren }) {
	return (
		<div class="detail-field">
			<span class="detail-label">{label}</span>
			<div class="detail-value">{children}</div>
		</div>
	);
}

function PlatformDetail({ fact }: { fact: PlatformFact }) {
	return (
		<>
			<div class="detail-head">
				<div>
					<span class="eyebrow">Platform instrument</span>
					<h2>{fact.name}</h2>
				</div>
				<span class={`status ${fact.available ? 'status-available' : 'status-unavailable'}`}>
					{fact.available ? 'AVAILABLE' : 'UNAVAILABLE'}
				</span>
			</div>
			<p class="detail-lede">{fact.does}</p>
			<div class="detail-fields">
				<DetailField label="Availability">
					{fact.available ? 'Usable in this deployment now.' : fact.state}
				</DetailField>
				<DetailField label="How it is invoked">
					<code class="wrap-code">{fact.invocation}</code>
				</DetailField>
				<DetailField label="What it returns">{fact.returns}</DetailField>
				<DetailField label="Recovery">{fact.recovery}</DetailField>
			</div>
			<p class="section-note">
				This entry is generated from the same runtime facts the planner manifest receives.
				It is a statement about this deployment, not about what Scout has achieved.
			</p>
		</>
	);
}

function FoundryDetail({
	detail,
	summary,
}: {
	detail: FoundryDetailPayload;
	summary: FoundryCapability | null;
}) {
	// THE DETAIL ROUTE RETURNS THE CANONICAL ROW; THE LIST ROW CARRIES THE
	// COMPUTED AVAILABILITY AND EXECUTION AGGREGATE. Merging keeps the pane from
	// saying "not projected" or "no execution" when the inventory row beside it
	// has the answer.
	const capability =
		detail.capability === undefined
			? undefined
			: ({ ...summary, ...detail.capability } as FoundryDetailPayload['capability']);
	if (capability === undefined) {
		return (
			<div class="empty-state">
				<p class="empty-title">No capability detail was returned</p>
				<p class="empty-note">{detail.reason ?? 'The registry accepted the id but returned no record.'}</p>
			</div>
		);
	}
	const runRows = detail.runs ?? [];
	const aggregate =
		capability.execution === undefined || capability.execution === null
			? runRows.length === 0
				? null
				: {
						total: runRows.length,
						succeeded: runRows.filter((run) => run.status === 'SUCCEEDED').length,
						failed: runRows.filter((run) =>
							['FAILED', 'TIMEOUT', 'ABANDONED'].includes(run.status),
						).length,
						refused: runRows.filter((run) => run.status === 'REFUSED').length,
						lastRunAt: runRows[0]?.completedAt ?? runRows[0]?.createdAt ?? null,
						lastStatus: runRows[0]?.status ?? null,
					}
			: capability.execution;
	const execution = aggregate;
	return (
		<>
			<div class="detail-head">
				<div>
					<span class="eyebrow">Foundry capability · {capability.origin.replaceAll('_', ' ')}</span>
					<h2>{capability.name}</h2>
				</div>
				<span class={`status status-${capability.status.toLowerCase()}`}>{capability.status}</span>
			</div>
			<p class="detail-lede">{capability.purpose}</p>
			<p class="detail-lede">{capability.whenToUse}</p>
			<div class="detail-fields">
				<DetailField label="Scope">
					{capability.shared || capability.delivery === 'SHARED_REGISTRY'
						? 'Shared registry — every authorized workspace'
						: capability.spaceId === null
							? 'Main Scout'
							: 'Workspace-local'}
				</DetailField>
				<DetailField label="Version">{String(capability.version)}</DetailField>
				<DetailField label="Source hash">
					<code class="wrap-code">{capability.sourceHash}</code>
				</DetailField>
				<DetailField label="Network">{capability.network}</DetailField>
				<DetailField label="Dependencies">
					{capability.dependencies.length === 0 ? 'None declared' : capability.dependencies.join(', ')}
				</DetailField>
				<DetailField label="Limits">
					{capability.limits === null
						? 'No limits recorded'
						: `${capability.limits.timeoutMs ?? '—'} ms · ${capability.limits.memoryMb ?? '—'} MB`}
				</DetailField>
				<DetailField label="Last verified">
					{capability.lastVerifiedAt === null ? 'Never verified by execution' : formatTurnTime(capability.lastVerifiedAt)}
				</DetailField>
				<DetailField label="Executions">
					{execution === null || execution === undefined
						? 'No execution recorded'
						: `${execution.total} total · ${execution.succeeded} succeeded · ${execution.failed} failed · ${execution.refused} refused`}
				</DetailField>
				<DetailField label="Last run">
					{execution?.lastRunAt === null || execution?.lastRunAt === undefined
						? 'Never run'
						: `${formatTurnTime(execution.lastRunAt)} — ${execution.lastStatus}`}
				</DetailField>
				<DetailField label="Availability">{capability.availability?.globalAvailability ?? 'not projected'}</DetailField>
			</div>
			{capability.availability !== undefined && (
				<Technical summary="Availability evidence">
					<ul class="plain-list">
						{capability.availability.evidence.map((line) => (
							<li key={line}>{line}</li>
						))}
					</ul>
					{capability.availability.ownerActionRequired && (
						<p class="notice small">{capability.availability.ownerActionNote}</p>
					)}
				</Technical>
			)}
			{capability.limitations.length > 0 && (
				<section class="detail-section">
					<h3>Limitations</h3>
					<p>{capability.limitations}</p>
				</section>
			)}
			{capability.inputSchema !== undefined && (
				<Technical summary="Input and output schema">
					<pre class="detail-code">{JSON.stringify(capability.inputSchema, null, 2)}</pre>
					{capability.outputSchema !== undefined && (
						<pre class="detail-code">{JSON.stringify(capability.outputSchema, null, 2)}</pre>
					)}
				</Technical>
			)}
			{capability.sourceCode !== undefined && (
				<Technical summary="Source">
					<pre class="detail-code">{capability.sourceCode}</pre>
				</Technical>
			)}
			{detail.runs !== undefined && detail.runs.length > 0 && (
				<section class="detail-section">
					<h3>Recent runs</h3>
					<ul class="detail-runs">
						{detail.runs.slice(0, 8).map((run) => (
							<li key={run.runId}>
								<span class={`status status-${run.status.toLowerCase()}`}>{run.status}</span>
								<span class="muted small">
									v{run.capabilityVersion} · {formatTurnTime(run.createdAt)}
									{run.durationMs === null ? '' : ` · ${run.durationMs} ms`}
								</span>
							</li>
						))}
					</ul>
				</section>
			)}
		</>
	);
}

export function CapabilityLibraryView({ capabilityId = null }: { capabilityId?: string | null }) {
	const [query, setQuery] = useState('');
	const [origin, setOrigin] = useState('all');
	const [availability, setAvailability] = useState('all');
	const library = useMemo(
		() => createLoadable<LibraryPayload>(() => readGateway<LibraryPayload>('/api/scout/gateway/capability-library')),
		[],
	);
	useEffect(() => {
		void library.load();
	}, [library]);
	const state = library.state.value;
	const data = state.data;

	const foundryDetail = useMemo(
		() =>
			capabilityId === null || capabilityId.startsWith('platform:')
				? null
				: createLoadable<FoundryDetailPayload>(() =>
						readGateway<FoundryDetailPayload>(
							`/api/scout/gateway/foundry/capabilities/${encodeURIComponent(capabilityId)}`,
						),
					),
		[capabilityId],
	);
	useEffect(() => {
		if (foundryDetail !== null) void foundryDetail.load();
	}, [foundryDetail]);

	if (state.status === 'failed') {
		return (
			<section class="view">
				<PageHeader id="library-heading" title="Capability Library" about={ABOUT} />
				{state.error !== null && (
					<Failure
						error={state.error}
						what="the capability inventory"
						onRetry={() => void library.load()}
					/>
				)}
			</section>
		);
	}

	const rows: Row[] = [];
	for (const fact of data?.platform ?? []) rows.push({ ...fact, kind: 'PLATFORM' });
	for (const capability of data?.foundry ?? []) rows.push({ ...capability, kind: 'FOUNDRY' });

	const selectedPlatform =
		capabilityId !== null && capabilityId.startsWith('platform:')
			? rows.find(
					(row) => row.kind === 'PLATFORM' && `platform:${row.name}` === capabilityId,
				)
			: null;
	const selectedFoundry =
		capabilityId !== null && !capabilityId.startsWith('platform:')
			? (rows.find(
					(row): row is { kind: 'FOUNDRY' } & FoundryCapability =>
						row.kind === 'FOUNDRY' && row.capabilityId === capabilityId,
				) ?? null)
			: null;
	const detailOpen = selectedPlatform !== undefined && selectedPlatform !== null;

	const filtered = rows.filter((row) => predicatePasses(row, query, origin, availability));

	const openRow = (row: Row): void => {
		const id = row.kind === 'PLATFORM' ? `platform:${row.name}` : row.capabilityId;
		navigate({ destination: 'foundry', section: 'capability', recordId: id });
	};

	const detailError = foundryDetail?.state.value;
	const summary = data?.summary;
	return (
		<section class={`view capability-library${capabilityId === null ? '' : ' has-detail'}`}>
			<PageHeader id="library-heading" title="Capability Library" about={ABOUT}>
				<p class="view-blurb">
					What this system can do, where each capability came from, and whether it has
					actually run. Platform instruments are built into the runtime; Foundry
					capabilities were registered, versioned and verified here.
				</p>
			</PageHeader>

			<div class="stat-grid">
				<div class="stat">
					<span class="stat-label">Platform instruments</span>
					<span class="stat-value">{summary?.platformTotal ?? '—'}</span>
					<span class="stat-note">{summary?.platformAvailable ?? '—'} available in this deployment</span>
				</div>
				<div class="stat">
					<span class="stat-label">Foundry capabilities</span>
					<span class="stat-value">{summary?.foundryTotal ?? '—'}</span>
					<span class="stat-note">{summary?.foundryVerified ?? '—'} verified</span>
				</div>
				<div class="stat">
					<span class="stat-label">Executed successfully</span>
					<span class="stat-value">{summary?.foundryExecuted ?? '—'}</span>
					<span class="stat-note">a refused run is not an execution</span>
				</div>
			</div>

			<section class="panel panel-flush" aria-labelledby="inventory-heading">
				<div class="section-head">
					<div>
						<h2 id="inventory-heading">Inventory</h2>
						<p class="section-note">
							{state.status === 'loading' && data === null
								? 'Reading the platform inventory and the Foundry registry…'
								: `${filtered.length} of ${rows.length} capabilities`}
						</p>
					</div>
					<Technical summary="What the columns mean">
						<p>
							<strong>Origin</strong> says where the capability came from: PLATFORM is
							compiled into this build; the Foundry origins are the closed vocabulary the
							registry stores. <strong>Status</strong> is the registry's own state, not a
							score. <strong>Availability</strong> is the runtime evidence the gateway
							projected; “not projected” means it could not be established.
						</p>
					</Technical>
				</div>

				<div class="toolbar" role="search">
					<label class="toolbar-field toolbar-field--grow">
						<span class="visually-hidden">Search capabilities</span>
						<input
							class="toolbar-input"
							type="search"
							placeholder="Search name, purpose or invocation…"
							value={query}
							onInput={(event) => setQuery((event.target as HTMLInputElement).value)}
						/>
					</label>
					<label class="toolbar-field">
						<span class="toolbar-label">Origin</span>
						<select
							class="toolbar-select"
							value={origin}
							onChange={(event) => setOrigin((event.target as HTMLSelectElement).value)}
						>
							<option value="all">All origins</option>
							<option value="PLATFORM">Platform</option>
							<option value="FOUNDRY">Foundry</option>
						</select>
					</label>
					<label class="toolbar-field">
						<span class="toolbar-label">State</span>
						<select
							class="toolbar-select"
							value={availability}
							onChange={(event) => setAvailability((event.target as HTMLSelectElement).value)}
						>
							<option value="all">All states</option>
							<option value="available">Available / verified</option>
							<option value="unavailable">Unavailable / unverified</option>
							<option value="executed">Has run</option>
							<option value="never-run">Never run</option>
						</select>
					</label>
				</div>

				{state.status === 'loading' && <p class="notice" role="status">Reading the capability inventory…</p>}
				{data?.wired === false && (
					<p class="notice">
						{data.reason ?? 'This deployment states no capability inventory.'}
					</p>
				)}
				{data?.platform === null && data?.platformReason !== null && (
					<p class="notice">{data?.platformReason}</p>
				)}
				{data?.foundryReason !== null && data?.foundryReason !== undefined && (
					<p class="notice">{data.foundryReason}</p>
				)}
				{state.status !== 'loading' && filtered.length === 0 && (
					<div class="empty-state">
						<p class="empty-title">
							{rows.length === 0 ? 'No capabilities on record' : 'No capability matches these filters'}
						</p>
						<p class="empty-note">
							{rows.length === 0
								? 'Neither a platform inventory nor a Foundry registry was returned. That is a deployment fact, not an empty registry.'
								: 'Clear the search or change the origin and state filters.'}
						</p>
					</div>
				)}

				{filtered.length > 0 && (
					<div class="table-wrap">
						<table class="data-table capability-table">
							<thead>
								<tr>
									<th scope="col">Capability</th>
									<th scope="col">Origin</th>
									<th scope="col">Scope</th>
									<th scope="col" class="numeric">Version</th>
									<th scope="col">Status</th>
									<th scope="col">Availability</th>
									<th scope="col" class="numeric">Runs ok/total</th>
									<th scope="col">Last used</th>
									<th scope="col" class="cell-action">
										<span class="visually-hidden">Inspect</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((row) => {
									const id = row.kind === 'PLATFORM' ? `platform:${row.name}` : row.capabilityId;
									return (
										<tr
											key={id}
											class={`row-link${capabilityId === id ? ' is-selected' : ''}`}
											onClick={() => openRow(row)}
										>
											<td class="cell-title">
												<span>{row.name}</span>
												<span class="cell-sub">
													{row.kind === 'PLATFORM' ? row.does : row.purpose}
												</span>
											</td>
											<td data-label="Origin">
												<span class={`origin origin-${row.kind.toLowerCase()}`}>{originLabel(row)}</span>
											</td>
											<td class="cell-muted" data-label="Scope">
												{scopeLabel(row, data?.workspaces ?? [])}
											</td>
											<td class="numeric" data-label="Version">
												{row.kind === 'PLATFORM' ? '—' : String(row.version)}
											</td>
											<td data-label="Status">
												<span class={`status status-${statusLabel(row).toLowerCase().replaceAll(' ', '-')}`}>
													{statusLabel(row)}
												</span>
											</td>
											<td class="cell-muted" data-label="Availability">
												{executableLabel(row)}
											</td>
											<td class="numeric" data-label="Runs ok/total">
												{row.kind === 'PLATFORM'
													? '—'
													: row.execution === null || row.execution === undefined
														? '0'
														: `${row.execution.succeeded}/${row.execution.total}`}
											</td>
											<td class="cell-date" data-label="Last used">
												{row.kind === 'PLATFORM'
													? '—'
													: row.execution?.lastRunAt === null || row.execution?.lastRunAt === undefined
														? 'Never'
														: formatTurnTime(row.execution.lastRunAt)}
											</td>
											<td class="cell-action">
												<button
													type="button"
													class="button ghost small"
													onClick={(event) => {
														event.stopPropagation();
														openRow(row);
													}}
												>
													Inspect
												</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{capabilityId !== null && (
				<aside class="detail-pane" aria-label="Capability detail">
					<div class="detail-pane-head">
						<span class="eyebrow">Capability detail</span>
						<button
							type="button"
							class="link-button"
							onClick={() => navigate({ destination: 'foundry', section: null, recordId: null })}
						>
							Close
						</button>
					</div>
					{detailOpen && selectedPlatform !== null && selectedPlatform.kind === 'PLATFORM' && (
						<PlatformDetail fact={selectedPlatform} />
					)}
					{!detailOpen &&
						foundryDetail !== null &&
						detailError?.status === 'failed' &&
						detailError.error !== null && (
							<Failure
								error={detailError.error as GatewayError}
								what="the capability detail"
								onRetry={() => void foundryDetail.load()}
							/>
						)}
					{!detailOpen && foundryDetail !== null && detailError?.status !== 'failed' && (
						<>
							{detailError?.status === 'loading' && <p class="notice">Reading the capability…</p>}
							{detailError?.data !== null && detailError?.data !== undefined && (
								<FoundryDetail
									detail={detailError.data}
									summary={
										selectedFoundry !== null && selectedFoundry !== undefined
											? selectedFoundry
											: null
									}
								/>
							)}
						</>
					)}
				</aside>
			)}
		</section>
	);
}

const ABOUT = {
	purpose: (
		<>
			The Capability Library is the product's honest inventory: the instruments this build
			provides, and the capabilities registered in Scout's Foundry. It answers what a
			capability is, where it came from, and whether it has actually executed.
		</>
	),
	how: (
		<>
			Search or filter the inventory. Select a row to open its detail; the address carries
			the capability id, so the pane can be bookmarked. Scout promotes its own proven
			capability into the shared registry automatically; no review tab or approval is
			involved.
		</>
	),
	limits: (
		<>
			<p>
				Registration is not execution. A REGISTERED capability has not run; a VERIFIED one
				has a successful recorded run. “Availability” reports the runtime evidence the
				gateway could establish, and “not projected” means it could not establish any.
			</p>
			<p>
				This screen reads the Main Scout lane. A workspace capability is visible here
				because the Owner is the reader; an agent's own runtime still receives only its
				workspace's capabilities and the shared registry.
			</p>
		</>
	),
};
